import React, { useState } from 'react';
import { Loader2, Percent, DollarSign, Layers } from 'lucide-react';
import { Button } from '../../../ui/button';
import { Input } from '../../../ui/input';
import { Label } from '../../../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../ui/dialog';

const PRICING_TIERS = {
  blackout: { base_price: 119, price_per_sqft: 5.50, label: 'Blackout' },
  solar: { base_price: 99, price_per_sqft: 5.00, label: 'Solar / Sunscreen' },
  'light-filtering': { base_price: 89, price_per_sqft: 4.50, label: 'Light Filtering' },
  sheer: { base_price: 79, price_per_sqft: 4.00, label: 'Sheer' },
  woven: { base_price: 129, price_per_sqft: 6.00, label: 'Woven / Natural' },
};

export const BulkPriceDialog = ({
  open, onOpenChange, mode, setMode, scope,
  percentage, setPercentage, flatPrice, setFlatPrice,
  materialCount, onApplyPercentage, onApplyFlat, saving,
  collectionPricingTier, onApplyTier,
}) => {
  const [selectedTier, setSelectedTier] = useState(collectionPricingTier || '');
  const tier = PRICING_TIERS[selectedTier];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="bulk-price-dialog">
        <DialogHeader>
          <DialogTitle>Bulk Price Update</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="flex gap-2 flex-wrap">
            <Button variant={mode === 'tier' ? 'default' : 'outline'} size="sm" className={mode === 'tier' ? 'bg-amber-600 hover:bg-amber-700' : ''} onClick={() => setMode('tier')} data-testid="price-mode-tier">
              <Layers className="w-3 h-3 mr-1" /> Apply Tier
            </Button>
            <Button variant={mode === 'flat' ? 'default' : 'outline'} size="sm" className={mode === 'flat' ? 'bg-amber-600 hover:bg-amber-700' : ''} onClick={() => setMode('flat')} data-testid="price-mode-flat">
              <DollarSign className="w-3 h-3 mr-1" /> Custom Price
            </Button>
            <Button variant={mode === 'percentage' ? 'default' : 'outline'} size="sm" className={mode === 'percentage' ? 'bg-amber-600 hover:bg-amber-700' : ''} onClick={() => setMode('percentage')} data-testid="price-mode-percentage">
              <Percent className="w-3 h-3 mr-1" /> Percentage
            </Button>
          </div>

          {mode === 'tier' ? (
            <>
              <p className="text-sm text-neutral-500">Apply a pricing tier to all {materialCount} materials in this collection</p>
              <div>
                <Label>Pricing Tier</Label>
                <select value={selectedTier} onChange={(e) => setSelectedTier(e.target.value)} className="w-full mt-1 rounded-md border border-neutral-300 p-2" data-testid="tier-select">
                  <option value="">Select a tier...</option>
                  {Object.entries(PRICING_TIERS).map(([id, t]) => <option key={id} value={id}>{t.label}</option>)}
                </select>
              </div>
              {tier && (
                <div className="p-3 rounded-lg bg-amber-50 border border-amber-100">
                  <p className="text-sm font-semibold text-amber-800 mb-1">{tier.label} Pricing</p>
                  <div className="grid grid-cols-2 gap-2 text-sm text-amber-700">
                    <span>Base price: <strong>${tier.base_price}</strong>/shade</span>
                    <span>Per sqft: <strong>${tier.price_per_sqft.toFixed(2)}</strong>/sqft</span>
                  </div>
                  <p className="text-xs text-amber-600 mt-2">Will set both base_price and price_per_sqft on all {materialCount} materials</p>
                </div>
              )}
            </>
          ) : mode === 'percentage' ? (
            <>
              <p className="text-sm text-neutral-500">Adjust all material prices in this {scope === 'brand' ? 'brand' : 'collection'} by a percentage</p>
              <div>
                <Label>Percentage Change</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input type="number" value={percentage} onChange={(e) => setPercentage(parseFloat(e.target.value) || 0)} placeholder="0" className="flex-1" data-testid="bulk-price-percentage-input" />
                  <span className="text-lg font-medium">%</span>
                </div>
                <p className="text-xs text-neutral-500 mt-1">Use positive for increase, negative for decrease (e.g., -10 for 10% off)</p>
              </div>
              {percentage !== 0 && (
                <div className={`p-3 rounded-lg ${percentage > 0 ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                  <p className="text-sm font-medium">{percentage > 0 ? '↑' : '↓'} All prices will be {percentage > 0 ? 'increased' : 'decreased'} by {Math.abs(percentage)}%</p>
                </div>
              )}
            </>
          ) : (
            <>
              <p className="text-sm text-neutral-500">Set custom base price and per-sqft rate for all materials</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Base Price ($)</Label>
                  <div className="flex items-center gap-1 mt-1">
                    <span className="text-sm font-medium">$</span>
                    <Input type="number" step="0.01" min="0" value={flatPrice} onChange={(e) => setFlatPrice(parseFloat(e.target.value) || 0)} placeholder="89" className="flex-1" data-testid="bulk-price-flat-input" />
                  </div>
                  <p className="text-xs text-neutral-400 mt-0.5">Flat fee per shade</p>
                </div>
                <div>
                  <Label>Per Sq Ft ($)</Label>
                  <div className="flex items-center gap-1 mt-1">
                    <span className="text-sm font-medium">$</span>
                    <Input type="number" step="0.01" min="0" id="flatPriceSqft" placeholder="4.50" className="flex-1" data-testid="bulk-price-sqft-input" />
                  </div>
                  <p className="text-xs text-neutral-400 mt-0.5">Area-based rate</p>
                </div>
              </div>
              {flatPrice > 0 && (
                <div className="p-3 rounded-lg bg-amber-50 text-amber-700">
                  <p className="text-sm font-medium">All {materialCount} materials → ${flatPrice.toFixed(2)} base</p>
                </div>
              )}
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          {mode === 'tier' ? (
            <Button onClick={() => onApplyTier?.(selectedTier)} disabled={saving || !selectedTier} className="bg-amber-600 hover:bg-amber-700" data-testid="apply-tier-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Layers className="w-4 h-4 mr-2" />}
              Apply Tier
            </Button>
          ) : mode === 'percentage' ? (
            <Button onClick={onApplyPercentage} disabled={saving || percentage === 0} className={percentage > 0 ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'} data-testid="apply-percentage-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Percent className="w-4 h-4 mr-2" />}
              Apply {percentage > 0 ? 'Increase' : 'Decrease'}
            </Button>
          ) : (
            <Button onClick={() => {
              const sqftEl = document.getElementById('flatPriceSqft');
              const sqftVal = parseFloat(sqftEl?.value) || 0;
              onApplyFlat(flatPrice, sqftVal);
            }} disabled={saving || flatPrice <= 0} className="bg-amber-600 hover:bg-amber-700" data-testid="apply-flat-price-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <DollarSign className="w-4 h-4 mr-2" />}
              Set Price
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BulkPriceDialog;
